/**
 * ChecksSection - Reusable component to display checks on detail pages
 * 
 * Used on: Vendor Detail, Bill Detail, PO Detail pages
 */
import React, { useState, useEffect } from 'react';
import { FileText, Loader2, Download, Eye, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { checksAPI } from '../../utils/api';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) return '$0.00';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Status badge component
const CheckStatusBadge = ({ status }) => {
  const configs = {
    draft: { label: 'Draft', className: 'bg-yellow-100 text-yellow-700' },
    printed: { label: 'Printed', className: 'bg-green-100 text-green-700' },
    void: { label: 'Void', className: 'bg-gray-100 text-gray-500 line-through' }
  };
  const config = configs[status] || configs.draft;
  
  return (
    <Badge className={config.className}>
      {config.label}
    </Badge>
  );
};

/**
 * ChecksSection Component
 * 
 * @param {string} vendorId - Filter by vendor ID
 * @param {string} billId - Filter by bill ID  
 * @param {string} poId - Filter by PO ID
 * @param {string} title - Section title (default: "Checks")
 * @param {number} limit - Max checks to show (default: 5)
 * @param {function} onViewAll - Callback when "View All" is clicked
 * @param {string} emptyMessage - Message when no checks found
 */
const ChecksSection = ({ 
  vendorId, 
  billId, 
  poId,
  title = "Checks",
  limit = 5,
  onViewAll,
  emptyMessage = "No checks found"
}) => {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    const fetchChecks = async () => {
      setLoading(true);
      try {
        const params = { page_size: limit };
        if (vendorId) params.vendor_id = vendorId;
        if (billId) params.bill_id = billId;
        if (poId) params.po_id = poId;
        
        const res = await checksAPI.getChecks(params);
        setChecks(res.checks || []);
        setTotal(res.total || 0);
      } catch (err) {
        console.error('Failed to fetch checks', err);
        setChecks([]);
      } finally {
        setLoading(false);
      }
    };

    if (vendorId || billId || poId) {
      fetchChecks();
    } else {
      setLoading(false);
    }
  }, [vendorId, billId, poId, limit]);

  const openCheckDetail = (check) => {
    setSelectedCheck(check);
    setShowDetailModal(true);
  };

  const downloadPdf = (checkId, e) => {
    e?.stopPropagation();
    window.open(`${API_BASE_URL}/api/checks/${checkId}/pdf`, '_blank');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-400" />
          {title}
        </h3>
        <div className="flex justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl border p-6" data-testid="checks-section">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-400" />
            {title}
            {total > 0 && (
              <span className="text-sm font-normal text-gray-500">({total})</span>
            )}
          </h3>
          {total > limit && onViewAll && (
            <Button variant="ghost" size="sm" onClick={onViewAll}>
              View All
              <ExternalLink className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>

        {checks.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p>{emptyMessage}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="pb-2 font-medium">Check #</th>
                  <th className="pb-2 font-medium">Date</th>
                  <th className="pb-2 font-medium text-right">Amount</th>
                  <th className="pb-2 font-medium">Status</th>
                  <th className="pb-2 font-medium">Bank Account</th>
                  <th className="pb-2 font-medium w-20"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {checks.map((check) => (
                  <tr 
                    key={check.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => openCheckDetail(check)}
                    data-testid={`check-row-${check.id}`}
                  >
                    <td className="py-3">
                      <span className="font-mono font-semibold text-blue-600">
                        #{check.check_number}
                      </span>
                    </td>
                    <td className="py-3 text-gray-600">
                      {formatDate(check.date)}
                    </td>
                    <td className="py-3 text-right font-medium">
                      {formatCurrency(check.amount_cents)}
                    </td>
                    <td className="py-3">
                      <CheckStatusBadge status={check.status} />
                    </td>
                    <td className="py-3 text-gray-600">
                      {check.bank_account_name || '—'}
                    </td>
                    <td className="py-3">
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openCheckDetail(check)}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {check.status !== 'void' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => downloadPdf(check.id, e)}
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Check Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Check #{selectedCheck?.check_number}
              {selectedCheck && <CheckStatusBadge status={selectedCheck.status} />}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCheck && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Date</span>
                  <p className="font-medium">{formatDate(selectedCheck.date)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Amount</span>
                  <p className="font-semibold text-lg">{formatCurrency(selectedCheck.amount_cents)}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Payee</span>
                  <p className="font-medium">{selectedCheck.payee_name}</p>
                  {selectedCheck.payee_address && (
                    <p className="text-gray-600 whitespace-pre-line text-xs mt-1">{selectedCheck.payee_address}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Amount in Words</span>
                  <p className="font-medium">{selectedCheck.amount_words}</p>
                </div>
                {selectedCheck.memo && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Memo</span>
                    <p>{selectedCheck.memo}</p>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">Bank Account</span>
                  <p>{selectedCheck.bank_account_name}</p>
                </div>
                {selectedCheck.printed_at && (
                  <div>
                    <span className="text-gray-500">Printed At</span>
                    <p>{formatDate(selectedCheck.printed_at)}</p>
                  </div>
                )}
              </div>
              
              {/* Line Items */}
              {selectedCheck.line_items && selectedCheck.line_items.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Line Items</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Description</th>
                          <th className="px-3 py-2 text-right font-medium">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {selectedCheck.line_items.map((item, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2">
                              {item.bill_number && <span className="text-gray-500 mr-1">Bill #{item.bill_number}</span>}
                              {item.po_number && <span className="text-gray-500 mr-1">PO #{item.po_number}</span>}
                              {item.description || '—'}
                            </td>
                            <td className="px-3 py-2 text-right">{formatCurrency(item.amount_cents)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Void Info */}
              {selectedCheck.status === 'void' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="font-medium text-red-700">Voided</p>
                  {selectedCheck.void_reason && (
                    <p className="text-red-600 text-sm mt-1">Reason: {selectedCheck.void_reason}</p>
                  )}
                  {selectedCheck.voided_at && (
                    <p className="text-red-500 text-xs mt-1">On {formatDate(selectedCheck.voided_at)}</p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
            {selectedCheck?.status !== 'void' && (
              <Button onClick={() => downloadPdf(selectedCheck.id)}>
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChecksSection;
